import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowUpRight, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEOMeta, { PAGE_META } from '../components/SEOMeta';
import { PersonSchema, OrganizationSchema } from '../components/JsonLdSchema';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HEADSHOT = "https://customer-assets-jai6qajn.emergentagent.net/job_cd729cf5-4469-4028-b6ba-a9c891131dbe/artifacts/dnctg08g_1780602847089.jpg";

const About = () => {
  const [bio, setBio] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/cms/about/public`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.content) setBio(d.content); })
      .catch(() => {});
  }, []);

  const paragraphs = bio
    ? bio.split(/\n\n+/).map(p => p.trim()).filter(Boolean)
    : [];

  const openingPara = paragraphs[0] || '';
  const bodyParas   = paragraphs.slice(1);

  return (
    <div className="bg-[#FAF8F3]" data-testid="about-page">
      <SEOMeta url="/about" {...PAGE_META.about} />
      <PersonSchema />
      <OrganizationSchema />

      {/* Hero — dark navy */}
      <section className="relative pt-36 pb-24 overflow-hidden bg-[#071B3A]">
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <span className="block text-[11px] tracking-[0.25em] uppercase text-[#C99316] font-sans font-semibold mb-6">About</span>
              <h1 className="font-serif font-light tracking-tighter leading-[0.94] mb-6">
                <span className="block text-[#FAF8F3]/75 text-3xl sm:text-4xl lg:text-5xl">Dr Promise</span>
                <span className="block text-[#FAF8F3] text-5xl sm:text-6xl lg:text-[72px]">Monday</span>
              </h1>
              <div className="flex items-center gap-3 mb-10">
                <div style={{ width: '22px', height: '1px', backgroundColor: 'rgba(201,147,22,0.5)' }} />
                <p className="text-[#FAF8F3]/60 text-sm tracking-[0.07em] font-sans font-light">
                  Paediatrician &bull; Author &bull; Founder, Third Culture Leadership
                </p>
              </div>

              {/* Opening paragraph from managed content */}
              <div className="space-y-5 text-[#FAF8F3]/82 font-light leading-[1.85] tracking-wide text-base max-w-xl">
                {openingPara ? (
                  <p data-testid="about-opening-para">{openingPara}</p>
                ) : (
                  <>
                    <p>A thought leader in cultural intelligence and inclusive leadership, dedicated to helping organisations navigate multicultural complexity with clarity and confidence.</p>
                    <p>His work focuses on creating environments where people honour their cultural identity whilst building genuine connections across difference.</p>
                  </>
                )}
              </div>

              <div className="mt-12 flex flex-wrap gap-5">
                <Link to="/speaking" className="inline-flex items-center gap-3 px-8 py-4 font-sans text-sm uppercase tracking-[0.15em] font-medium transition-all duration-300 hover:bg-[#C99316] hover:text-[#071B3A]" style={{ border: '1px solid rgba(201,147,22,0.7)', color: '#C99316' }}>
                  <span>Book for Speaking</span> <ArrowRight size={16} />
                </Link>
                <a href="https://www.linkedin.com/in/promisemonday" target="_blank" rel="noopener noreferrer" className="pb-1 text-sm uppercase tracking-[0.15em] text-[#FAF8F3]/40 font-sans hover:text-[#C99316] transition-all duration-500 inline-flex items-center gap-2" style={{ borderBottom: '1px solid rgba(250,248,243,0.15)' }}>
                  <Linkedin size={14} /> LinkedIn <ArrowUpRight size={12} />
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <div className="relative">
                <img
                  src={HEADSHOT}
                  alt="Dr Promise Monday"
                  className="relative w-full"
                  style={{ filter: 'saturate(0.8)' }}
                  data-testid="about-headshot"
                />
              </div>
              <div className="mt-8 pl-5" style={{ borderLeft: '2px solid rgba(201,147,22,0.3)' }}>
                <p className="font-serif text-xl text-[#FAF8F3]/50 font-light tracking-tight italic leading-relaxed">"Indeed, a third culture is possible."</p>
                <p className="text-[#C99316]/40 text-[10px] mt-3 uppercase tracking-[0.2em]">Promise Monday</p>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/15 to-transparent" />
      </section>

      {/* Full Biography — rendered from managed content */}
      {bodyParas.length > 0 && (
        <section className="py-24" style={{ backgroundColor: '#FAF8F3' }} data-testid="about-biography-section">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <span className="block text-[11px] tracking-[0.25em] uppercase text-[#C99316] font-sans font-semibold mb-10">Biography</span>
            <div className="space-y-6" data-testid="about-bio-paragraphs">
              {bodyParas.map((para, i) => (
                <p
                  key={i}
                  className="font-serif font-light leading-[1.95] tracking-wide text-base"
                  style={{ color: 'rgba(7,27,58,0.72)' }}
                >
                  {para}
                </p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Credentials */}
      <section className="py-20 relative" style={{ backgroundColor: bodyParas.length > 0 ? 'rgba(7,27,58,0.03)' : '#FAF8F3' }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="block text-[11px] tracking-[0.25em] uppercase text-[#C99316] font-sans font-semibold mb-10">Credentials</span>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { num: '01', title: 'Author & Publisher', desc: 'Published "Cultural Fluency Without Compromise" through Promond Publishing. Writing that gives leaders the language for what they experience.' },
              { num: '02', title: 'Cultural Fluency Educator', desc: 'Developing practical frameworks and resources to help leaders and professionals understand cultural difference, communicate more effectively, and work well across cultures.' },
              { num: '03', title: 'Speaker & Facilitator', desc: 'Available for talks, workshops, and facilitated learning sessions on cultural fluency, multicultural leadership, cross-cultural communication, and working effectively across difference.' },
            ].map((item) => (
              <div key={item.num} className="bg-white p-8 md:p-10 transition-all duration-500 group" style={{ border: '1px solid rgba(7,27,58,0.08)' }} onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(201,147,22,0.25)'; }} onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(7,27,58,0.08)'; }}>
                <span className="font-serif text-[48px] font-light leading-none block mb-4 transition-colors duration-700" style={{ color: 'rgba(201,147,22,0.12)' }}>{item.num}</span>
                <h3 className="font-serif text-xl text-[#071B3A] font-light tracking-tight mb-3">{item.title}</h3>
                <p className="font-sans text-base font-light leading-[1.8] tracking-wide" style={{ color: 'rgba(7,27,58,0.5)' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: '#071B3A' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-4xl sm:text-5xl text-[#FAF8F3] font-light tracking-tighter leading-[1.05] mb-6">Ready to develop<br/>cultural fluency?</h2>
          <div className="flex flex-wrap justify-center gap-5 mt-8">
            <Link to="/cfq-assessment" className="inline-flex items-center gap-3 px-8 py-4 font-sans text-sm uppercase tracking-[0.15em] font-medium transition-all duration-300 hover:bg-[#C99316] hover:text-[#071B3A]" style={{ border: '1px solid rgba(201,147,22,0.7)', color: '#C99316' }}>
              <span>Take CFQ Assessment</span> <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="pb-1 text-sm uppercase tracking-[0.15em] font-sans transition-all duration-500 hover:text-[#C99316]" style={{ borderBottom: '1px solid rgba(250,248,243,0.2)', color: 'rgba(250,248,243,0.5)' }}>
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
